import { z } from "zod";

export const UsersTableFilterParamsSchema = z.object({
  page: z.number().optional(),
  size: z.number().optional(),
  sort_key: z.string().optional(),
  sort_order: z.string().optional(),
  users: z.array(z.string()).optional(),
  roles: z.array(z.string()).optional(),
  insights: z.array(z.string()).optional(),
  instance_ids: z.array(z.string()).optional(),
  search: z.string().optional(),
});

export type UsersTableFilterParams = z.infer<typeof UsersTableFilterParamsSchema>;

export const RolesTableFilterParamsSchema = z.object({
  page: z.number().optional(),
  size: z.number().optional(),
  sort_key: z.string().optional(),
  sort_order: z.string().optional(),
  roles: z.array(z.string()).optional(),
  insights: z.array(z.string()).optional(),
  instance_ids: z.array(z.string()).optional(),
  search: z.string().optional(),
});

export type RolesTableFilterParams = z.infer<typeof RolesTableFilterParamsSchema>;

// Governance Summary
export const GovernanceSummaryItemSchema = z.object({
  label: z.string(),
  value: z.union([z.string(), z.number()]),
});

export const GovernanceSummarySchema = z.array(GovernanceSummaryItemSchema);

export type GovernanceSummary = z.infer<typeof GovernanceSummarySchema>;

// Governance Filters
export const GovernanceUserFiltersSchema = z.object({
  user_names: z.array(z.string()),
  default_roles: z.array(z.string()),
  insights: z.array(z.string()),
});

export const GovernanceRoleFiltersSchema = z.object({
  role_names: z.array(z.string()),
  insights: z.array(z.string()),
});

export enum GovernanceFilterEntityTypes {
  UserNames = "users_user_names",
  UsersDefaultRoles = "users_default_roles",
  UsersInsights = "users_insights",
  RoleNames = "roles_role_names",
  RolesInsights = "roles_insights",
}

export const GovernanceFilterParamsSchema = z.object({
  entity_type: z.nativeEnum(GovernanceFilterEntityTypes),
  prefix: z.string().optional(),
  search: z.string().optional(),
  page: z.number(),
});

export type GovernanceFilterParams = z.infer<typeof GovernanceFilterParamsSchema>;

export const GovernanceFiltersSchema = z.object({
  users: GovernanceUserFiltersSchema,
  roles: GovernanceRoleFiltersSchema,
  ok: z.boolean(),
  total: z.number(),
  page: z.number(),
  size: z.number(),
  total_pages: z.number(),
});

export type GovernanceFilters = z.infer<typeof GovernanceFiltersSchema>;

// Governance User
export const GovernanceUserSchema = z.object({
  user_name: z.string(),
  total_cost: z.number(),
  max_cost: z.number(),
  avg_cost: z.number(),
  num_of_queries: z.string(),
  num_of_query_groups: z.string(),
  num_of_adhoc_queries: z.string(),
  insights: z.array(z.string()).optional().nullable(),
  instance_id: z.union([z.string(), z.number()]),
  default_role: z.string().nullable(),
});

export const GovernanceUsersResponseSchema = z.object({
  items: z.array(GovernanceUserSchema),
  total: z.number(),
  pages: z.number(),
});

export type GovernanceUser = z.infer<typeof GovernanceUserSchema>;
export type GovernanceUsersResponse = z.infer<typeof GovernanceUsersResponseSchema>;

// Governance Role
export const GovernanceRoleSchema = z.object({
  role_name: z.string(),
  total_cost: z.number(),
  max_cost: z.number(),
  avg_cost: z.number(),
  num_of_queries: z.string(),
  num_of_query_groups: z.string(),
  num_of_adhoc_queries: z.string(),
  insights: z.array(z.string()).optional().nullable(),
  instance_id: z.union([z.string(), z.number()]),
  default_role: z.string().optional(),
});

export const GovernanceRolesResponseSchema = z.object({
  items: z.array(GovernanceRoleSchema),
  total: z.number(),
  pages: z.number(),
});

export type GovernanceRole = z.infer<typeof GovernanceRoleSchema>;
export type GovernanceRolesResponse = z.infer<typeof GovernanceRolesResponseSchema>;

// Insight Detail Schema
export const InsightDetailSchema = z.object({
  label: z.string(),
  reason: z.string().optional().nullable(),
});

// Governance User Details
export const GovernanceUserDetailsSchema = z.object({
  user_name: z.string(),
  num_of_queries: z.number(),
  last_success_login: z.string().optional().nullable(),
  insights: z.array(InsightDetailSchema).optional().nullable(),
});

export type GovernanceUserDetails = z.infer<typeof GovernanceUserDetailsSchema>;

// Governance Role Details
export const GovernanceRoleDetailsSchema = z.object({
  role_name: z.string(),
  num_of_queries: z.number(),
  insights: z.array(InsightDetailSchema).optional().nullable(),
});

export type GovernanceRoleDetails = z.infer<typeof GovernanceRoleDetailsSchema>;

// Governance Cost Graph
export const GovernanceCostGraphItemSchema = z.object({
  date: z.string(),
  cost: z.number(),
});

export const GovernanceCostGraphSchema = z.object({
  items: z.array(GovernanceCostGraphItemSchema),
});

export type GovernanceCostGraph = z.infer<typeof GovernanceCostGraphSchema>;
